// @pages/workspace/DocumentSelection.page.tsx

import React, { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { renderRoute, routeDocumentCreation, routeReviewWithDocument } from "@navigation/typedRoutes";
import type { Route } from "@navigation/typedRoutes";
import DocumentSelectionComponent from "@view/workspace/DocumentSelection";
import { useLifecycleClient } from "@contexts/LifecycleClient.context";
import { useDocumentContext } from "@contexts/Document.context";
import { useDocumentListContext } from "@contexts/DocumentList.context";
import { useLoadingController } from "@contexts/Loading.context";
import { useOnInit } from "@hooks/effects/useIoEffect";
import type { DocumentSelectionKernel } from "@ps-bridge/contracts/documentSelection";
import { buildDocumentSelectionViewProps } from "@ps-bridge/documentSelection.program";
import { ok, type Ok } from "@ps-bridge/ok";
import { mkBlobHandler, mkNavigate, mkRouteBuilder, mkTaskRunner } from "@ps-bridge/capabilities";
import type { IOts, TaskTs } from "@ps-bridge/interop";
import { ioEffect } from '@utils/effects/branded';
import { useRunTaskWithError } from "@hooks/effects/useTaskEffect";
import DocumentSelectionPagePanel from "@view/panels/workspace/document-selection/DocumentSelectionPage.panel";

const DocumentSelectionPage: React.FC = () => {
  const navigate = useNavigate();
  const { availableDocuments: availableDocumentsHandle } = useDocumentListContext();
  const lifecycleClient = useLifecycleClient();
  const { document: documentHandle } = useDocumentContext();
  const loading = useLoadingController();
  const runTaskWithError = useRunTaskWithError();

  const navigateToRoute = useCallback(
    (route: Route): IOts<Ok> =>
      ioEffect(() => {
        navigate(renderRoute(route));
        return ok;
      }),
    [navigate],
  );

  const downloadDocument = useCallback(
    (blob: Blob) =>
      (title: string): IOts<Ok> =>
        ioEffect(() => {
          const url = window.URL.createObjectURL(blob);
          const anchor = document.createElement("a");
          anchor.href = url;
          anchor.download = `${title}.md`;
          document.body.appendChild(anchor);
          anchor.click();
          document.body.removeChild(anchor);
          window.URL.revokeObjectURL(url);
          return ok;
        }),
    [],
  );

  const runTaskWithErrorIo = useCallback(
    (task: TaskTs<Ok>) =>
      ioEffect(() => {
        runTaskWithError(task).then(() => ok);
        return ok;
      }),
    [runTaskWithError],
  );

  const kernel: DocumentSelectionKernel = useMemo(
    () => ({
      loading,
      lifecycle: lifecycleClient,
      navigate: mkNavigate(navigateToRoute),
      reviewRoute: mkRouteBuilder(routeReviewWithDocument),
      document: documentHandle,
      availableDocuments: availableDocumentsHandle,
      downloadDocument: mkBlobHandler(downloadDocument),
      taskRunner: mkTaskRunner(runTaskWithErrorIo),
    }),
    [
      loading,
      lifecycleClient,
      navigateToRoute,
      documentHandle,
      availableDocumentsHandle,
      downloadDocument,
      runTaskWithErrorIo,
    ],
  );

  const viewProps = useMemo(
    () =>
      buildDocumentSelectionViewProps({
        kernel,
        documentCreationRoute: routeDocumentCreation,
      }),
    [kernel],
  );

  useOnInit(viewProps.onInit);
  const bodyView = <DocumentSelectionComponent {...viewProps} />;

  return <DocumentSelectionPagePanel bodyView={bodyView} />;
};

export default DocumentSelectionPage;
